/**
 * AI data consent + reports of AI-generated replies — the contract shared by
 * the backend and speak (store review: Apple 5.1.2(i) consent before personal
 * data goes to third-party AI; Google Play "AI-Generated Content" in-app
 * reporting).
 *
 * The CONSENT VERSION names the text the person accepted. Bump it only when
 * the consent screen changes what it says is sent or to whom: every account
 * then has to accept again (the backend treats a consent of another version as
 * missing), and the client sends the version it showed so a stale bundle
 * cannot record a consent for a text it never displayed.
 */
import { z } from 'zod';

/** The consent text currently shown (speak `aiConsentCopy.ts`: the checkbox + its details sheet). */
export const AI_DATA_CONSENT_VERSION = '2026-10-22';

/** Body of `POST /api/auth/me/ai-data-consent`. */
export const aiDataConsentBodySchema = z.object({ version: z.string().min(1).max(32) }).strict();

/** Where the reported reply was shown. */
export const AI_CONTENT_REPORT_SURFACES = [
  'conversation',
  'session_history',
  'theory_chat',
] as const;
export type AiContentReportSurface = (typeof AI_CONTENT_REPORT_SURFACES)[number];

/** Why it was reported. */
export const AI_CONTENT_REPORT_REASONS = ['offensive', 'incorrect', 'other'] as const;
export type AiContentReportReason = (typeof AI_CONTENT_REPORT_REASONS)[number];

/** Bounds of a report (the SQL CHECKs mirror them). */
export const AI_CONTENT_REPORT_LIMITS = { comment: 1000, text: 8000 } as const;

/**
 * Body of `POST /api/modules/english/ai-content-reports`. `text` is the reply
 * as the person saw it (the snapshot staff reviews); `sessionId` / `turnIndex`
 * point at the voice session turn when the surface has one.
 */
export const aiContentReportBodySchema = z
  .object({
    surface: z.enum(AI_CONTENT_REPORT_SURFACES),
    reason: z.enum(AI_CONTENT_REPORT_REASONS),
    text: z.string().trim().min(1).max(AI_CONTENT_REPORT_LIMITS.text),
    comment: z.string().trim().max(AI_CONTENT_REPORT_LIMITS.comment).optional(),
    sessionId: z.string().uuid().optional(),
    turnIndex: z.number().int().min(0).max(100_000).optional(),
  })
  .strict();
export type AiContentReportInput = z.infer<typeof aiContentReportBodySchema>;
